import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Database, Eye, FileText, Bot, Building2, Clock, ExternalLink } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface VectorStore {
  id: string;
  organization_id: string;
  agent_id: string | null;
  vector_store_id: string;
  created_at: string;
  organization_name: string | null;
  agent_name: string | null;
}

interface VectorStoreFile {
  id: string;
  object: string;
  usage_bytes: number;
  created_at: number;
  vector_store_id: string;
  status: string;
  last_error: any;
  chunking_strategy: any;
  attributes: any;
}

interface FileContent {
  file_id: string;
  filename: string;
  attributes: Record<string, any>;
  object: string;
  data: Array<{ type: string; text: string }>;
  has_more: boolean;
  next_page: string | null;
}

const VectorStoreDebugPage = () => {
  const [selectedVectorStore, setSelectedVectorStore] = useState<VectorStore | null>(null);
  const [vectorStoreFiles, setVectorStoreFiles] = useState<VectorStoreFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const { toast } = useToast();

  // Fetch all vector stores with organization and agent information
  const { data: vectorStores, isLoading, error } = useQuery({
    queryKey: ["vector-stores-debug"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vector_store_mappings")
        .select(`
          *,
          organizations!vector_store_mappings_organization_id_fkey(name),
          agents!vector_store_mappings_agent_id_fkey(agent_name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data.map(item => ({
        id: item.id,
        organization_id: item.organization_id,
        agent_id: item.agent_id,
        vector_store_id: item.vector_store_id,
        created_at: item.created_at || "",
        organization_name: item.organizations?.name || null,
        agent_name: item.agents?.agent_name || null,
      }));
    },
  });

  const handleViewFiles = async (vectorStore: VectorStore) => {
    setIsLoadingFiles(true);
    setSelectedVectorStore(vectorStore);
    setFilesModalOpen(true);

    try {
      const { data, error } = await supabase.functions.invoke('admin-vector-store-debug', {
        body: { 
          action: 'list-files',
          vectorStoreId: vectorStore.vector_store_id 
        }
      });

      if (error) {
        throw new Error(`Failed to fetch files: ${error.message}`);
      }

      if (data?.error) {
        throw new Error(`Failed to fetch files: ${data.error}`);
      }

      setVectorStoreFiles(data?.data?.data || []);
    } catch (error) {
      console.error("Error fetching vector store files:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch vector store files",
        variant: "destructive",
      });
      setVectorStoreFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleViewContent = async (file: VectorStoreFile) => {
    if (!selectedVectorStore) return;
    
    setIsLoadingContent(true);
    setContentModalOpen(true);
    setFileContent(null);

    try {
      const { data, error } = await supabase.functions.invoke('admin-vector-store-debug', {
        body: { 
          action: 'get-file-content',
          vectorStoreId: selectedVectorStore.vector_store_id,
          fileId: file.id
        }
      });

      if (error) {
        throw new Error(`Failed to fetch file content: ${error.message}`);
      }

      if (data?.error) {
        throw new Error(`Failed to fetch file content: ${data.error}`);
      }

      setFileContent(data?.data || null);
    } catch (error) {
      console.error("Error fetching file content:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch file content",
        variant: "destructive",
      });
    } finally {
      setIsLoadingContent(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <MainLayout>
        <SuperAdminLayout>
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">Loading vector stores...</div>
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <SuperAdminLayout>
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-red-600">
              Error loading vector stores: {error.message}
            </div>
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SuperAdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Vector Store Debug</h1>
            <p className="text-muted-foreground">
              Debug vector stores and their files to troubleshoot retrieval issues
            </p>
          </div>

          <div className="grid gap-6">
            {vectorStores?.map((vectorStore) => (
              <Card key={vectorStore.id} className="w-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Database className="h-5 w-5 text-blue-600" />
                      <div>
                        <CardTitle className="text-lg">
                          Vector Store: {vectorStore.vector_store_id}
                        </CardTitle>
                        <CardDescription>
                          ID: {vectorStore.id}
                        </CardDescription>
                      </div>
                    </div>
                    <Button 
                      onClick={() => handleViewFiles(vectorStore)}
                      variant="outline"
                      className="flex items-center space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View Files</span>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">Organization:</span>
                      <span className="text-sm">{vectorStore.organization_name || "Unknown"}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Bot className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">Agent:</span>
                      <span className="text-sm">
                        {vectorStore.agent_name ? (
                          <Badge variant="outline">{vectorStore.agent_name}</Badge>
                        ) : (
                          <Badge variant="secondary">Organization Level</Badge>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">Created:</span>
                      <span className="text-sm">
                        {new Date(vectorStore.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Files Modal */}
        <Dialog open={filesModalOpen} onOpenChange={setFilesModalOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Vector Store Files</span>
              </DialogTitle>
              <DialogDescription>
                {selectedVectorStore && (
                  <>
                    Vector Store: {selectedVectorStore.vector_store_id} 
                    <br />
                    Organization: {selectedVectorStore.organization_name}
                    {selectedVectorStore.agent_name && (
                      <>
                        <br />
                        Agent: {selectedVectorStore.agent_name}
                      </>
                    )}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="max-h-[60vh]">
              {isLoadingFiles ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p>Loading files...</p>
                  </div>
                </div>
              ) : vectorStoreFiles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No files found in this vector store
                </div>
              ) : (
                <div className="space-y-3">
                  {vectorStoreFiles.map((file) => (
                    <div 
                      key={file.id} 
                      className="border rounded-lg p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">File ID: {file.id}</span>
                          <Badge 
                            variant={file.status === 'completed' ? 'default' : 
                                   file.status === 'failed' ? 'destructive' : 'secondary'}
                          >
                            {file.status}
                          </Badge>
                        </div>
                        <Button
                          onClick={() => handleViewContent(file)}
                          variant="outline"
                          size="sm"
                          className="flex items-center space-x-1"
                        >
                          <ExternalLink className="h-3 w-3" />
                          <span>View Content</span>
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Size:</span> {formatBytes(file.usage_bytes)}
                        </div>
                        <div>
                          <span className="font-medium">Created:</span> {formatDate(file.created_at)}
                        </div>
                        <div>
                          <span className="font-medium">Chunking:</span> {file.chunking_strategy?.type || 'Unknown'}
                        </div>
                        <div>
                          <span className="font-medium">Vector Store:</span> {file.vector_store_id}
                        </div>
                      </div>
                      {file.last_error && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                          <strong>Error:</strong> {JSON.stringify(file.last_error, null, 2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* File Content Modal */}
        <Dialog open={contentModalOpen} onOpenChange={setContentModalOpen}>
          <DialogContent className="max-w-6xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>File Content</span>
              </DialogTitle>
              <DialogDescription>
                {fileContent && (
                  <>
                    File: {fileContent.filename || fileContent.file_id}
                    <br />
                    File ID: {fileContent.file_id}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="max-h-[60vh]">
              {isLoadingContent ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p>Loading file content...</p>
                  </div>
                </div>
              ) : fileContent ? (
                <div className="space-y-4">
                  {/* Debug Information */}
                  {(fileContent as any).debug && (
                    <div className="border rounded-lg p-4 bg-yellow-50">
                      <h4 className="font-medium mb-2">Debug Information</h4>
                      <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                        {JSON.stringify((fileContent as any).debug, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {/* Raw Response Data */}
                  <details className="border rounded-lg p-4">
                    <summary className="cursor-pointer font-medium mb-2">
                      View Raw OpenAI Response
                    </summary>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-60">
                      {JSON.stringify(fileContent, null, 2)}
                    </pre>
                  </details>

                  {fileContent.attributes && Object.keys(fileContent.attributes).length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium mb-2">File Attributes</h4>
                      <pre className="text-sm bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(fileContent.attributes, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2">Content ({fileContent.data?.length || 0} chunks)</h4>
                    {fileContent.data && fileContent.data.length > 0 ? (
                      <div className="space-y-3">
                        {fileContent.data?.map((chunk, index) => (
                          <div key={index} className="border-l-4 border-blue-200 pl-4">
                            <div className="text-xs text-muted-foreground mb-1">
                              Chunk {index + 1} ({chunk.type})
                            </div>
                            <div className="text-sm bg-gray-50 p-3 rounded whitespace-pre-wrap">
                              {chunk.text}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground text-sm">
                        No content chunks found. Check the raw response above for debugging.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No content available
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default VectorStoreDebugPage; 